import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetIdentity, useNotify } from "ra-core";
import { useLocation, useNavigate } from "react-router";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { Check, Loader2, MessageCircle, Plus, Send, Sparkles, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { appendDictated, MicButton } from "../misc/dictation";

// LDF: Claude-Chat unten rechts. Anfragen gehen in claude_requests, der Worker auf dem Mac mini beantwortet sie.
// Vorgeschlagene Änderungen werden erst nach Klick auf „Ausführen“ mit den Rechten des angemeldeten Nutzers geschrieben.

export type Proposal = { id: string; action: string; label: string; params: Record<string, unknown>; done: boolean };

export type ClaudeRequest = {
  id: number;
  thread_id: string;
  prompt: string;
  status: "wartet" | "laeuft" | "fertig" | "fehler" | "abgebrochen";
  answer: string | null;
  proposals: Proposal[];
  error: string | null;
  created_at: string;
};

const THREAD_KEY = "ldf.claude.thread";

const newThreadId = () => crypto.randomUUID();

const readThread = () => {
  try {
    return localStorage.getItem(THREAD_KEY) ?? newThreadId();
  } catch {
    return newThreadId();
  }
};

// Aktuelle Seite und geöffneter Datensatz als Kontext für Claude
export const pageContext = (pathname: string) => {
  const m = pathname.match(/^\/(companies|contacts|deals)\/(\d+)/);
  return {
    path: pathname,
    ...(m ? { resource: m[1], id: Number(m[2]) } : {}),
    today: new Date().toISOString().slice(0, 10),
  };
};

const COMPANY_FIELDS = new Set([
  "phone_number", "website", "address", "zipcode", "city", "district", "description", "sector", "lead_score",
  "capacity", "access_info", "parking_info", "loading_info", "setup_rules", "venue_contact_info",
]);

// Führt eine vorgeschlagene Aktion aus (nur die freigegebenen Typen, mit Nutzerrechten/RLS)
export const executeProposal = async (proposal: Proposal, salesId: number | string | undefined) => {
  const supabase = getSupabaseClient();
  const p = proposal.params as Record<string, never>;
  const now = new Date().toISOString();
  const check = ({ error }: { error: { message: string } | null }) => {
    if (error) throw new Error(error.message);
  };
  switch (proposal.action) {
    case "create_task":
      check(await supabase.from("tasks").insert({ contact_id: p.contact_id, type: p.type ?? "sonstiges", text: p.text, due_date: p.due_date ?? now, sales_id: salesId }));
      return;
    case "create_contact_note":
      check(await supabase.from("contact_notes").insert({ contact_id: p.contact_id, text: p.text, date: now, sales_id: salesId }));
      await supabase.from("contacts").update({ last_seen: now }).eq("id", p.contact_id);
      return;
    case "create_deal_note":
      check(await supabase.from("deal_notes").insert({ deal_id: p.deal_id, text: p.text, date: now, sales_id: salesId }));
      return;
    case "update_event_status": {
      const { data } = await supabase.from("market_events").select("notes").eq("id", p.event_id).single();
      const notes = p.note ? [data?.notes, p.note].filter(Boolean).join("\n") : data?.notes;
      check(await supabase.from("market_events").update({ status: p.status, notes, updated_at: now }).eq("id", p.event_id));
      return;
    }
    case "update_company": {
      const fields = Object.fromEntries(Object.entries((p.fields ?? {}) as Record<string, unknown>).filter(([k]) => COMPANY_FIELDS.has(k)));
      if (!Object.keys(fields).length) throw new Error("Keine erlaubten Felder");
      check(await supabase.from("companies").update(fields).eq("id", p.company_id));
      return;
    }
    case "create_company":
      check(await supabase.from("companies").insert({ name: p.name, website: p.website ?? null, sector: p.sector ?? null, sales_id: salesId, created_at: now }));
      return;
    case "open_view":
      return;
    default:
      throw new Error(`Unbekannte Aktion ${proposal.action}`);
  }
};

export const ProposalList = ({ request }: { request: ClaudeRequest }) => {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  if (!request.proposals?.some((p) => p.action !== "open_view")) return null;

  const run = async (proposal: Proposal) => {
    setBusy(proposal.id);
    try {
      await executeProposal(proposal, identity?.id);
      const proposals = request.proposals.map((x) => (x.id === proposal.id ? { ...x, done: true } : x));
      await getSupabaseClient().from("claude_requests").update({ proposals }).eq("id", request.id);
      notify("Erledigt", { type: "success" });
      queryClient.invalidateQueries();
    } catch (e) {
      notify(`Konnte nicht ausgeführt werden: ${e instanceof Error ? e.message : String(e)}`, { type: "error" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-2 space-y-1">
      <div className="text-[11px] font-medium text-muted-foreground">Vorschläge – erst nach Klick ausgeführt:</div>
      {request.proposals.filter((p) => p.action !== "open_view").map((proposal) => (
        <div key={proposal.id} className="flex items-start gap-2 rounded-md border border-border p-2 text-xs">
          <span className="flex-1">{proposal.label}</span>
          {proposal.done ? (
            <span className="inline-flex items-center gap-1 text-green-700">
              <Check className="h-3.5 w-3.5" /> erledigt
            </span>
          ) : (
            <Button size="sm" className="h-6 px-2 text-xs" disabled={busy === proposal.id} onClick={() => run(proposal)}>
              {busy === proposal.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Ausführen"}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

export const useClaudeThread = (threadId: string) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["claude-thread", threadId],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("claude_requests")
        .select("id,thread_id,prompt,status,answer,proposals,error,created_at")
        .eq("thread_id", threadId)
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as ClaudeRequest[];
    },
    // Solange Claude arbeitet, regelmäßig nachsehen (zusätzlich zu Realtime)
    refetchInterval: (q) =>
      (q.state.data as ClaudeRequest[] | undefined)?.some((r) => r.status === "wartet" || r.status === "laeuft") ? 3000 : false,
  });

  useEffect(() => {
    const channel = getSupabaseClient()
      .channel(`claude-${threadId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "claude_requests", filter: `thread_id=eq.${threadId}` }, () =>
        queryClient.invalidateQueries({ queryKey: ["claude-thread", threadId] }),
      )
      .subscribe();
    return () => {
      getSupabaseClient().removeChannel(channel);
    };
  }, [threadId, queryClient]);

  return query;
};

export const sendClaudeRequest = async ({
  threadId,
  prompt,
  salesId,
  context,
}: {
  threadId: string;
  prompt: string;
  salesId: number | string;
  context: Record<string, unknown>;
}) => {
  const { error } = await getSupabaseClient()
    .from("claude_requests")
    .insert({ thread_id: threadId, prompt, sales_id: salesId, context });
  if (error) throw error;
};

export const Markdown = ({ text }: { text: string }) => {
  const html = useMemo(() => DOMPurify.sanitize(marked.parse(text, { async: false }) as string), [text]);
  return (
    <div
      className="prose prose-sm max-w-none text-sm [&_a]:underline [&_li]:my-0 [&_ol]:my-1 [&_p]:my-1 [&_table]:text-xs [&_td]:border [&_td]:px-1 [&_th]:border [&_th]:px-1 [&_ul]:my-1"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const STATUS_TEXT: Record<string, string> = {
  wartet: "Wartet auf den Worker …",
  laeuft: "Claude arbeitet …",
  fehler: "Fehler",
  abgebrochen: "Abgebrochen",
};

export const ClaudeChat = () => {
  const { identity } = useGetIdentity();
  const location = useLocation();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [threadId, setThreadId] = useState(readThread);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { data: requests } = useClaudeThread(threadId);
  const navigate = useNavigate();

  // Ansichtswechsel (open_view) sofort umsetzen – nur Navigation, keine Datenänderung.
  // Bereits ausgeführte Vorschläge merken, damit ein fehlgeschlagenes „done“-Speichern nicht erneut umleitet.
  const handledViews = useRef(new Set<string>());
  useEffect(() => {
    for (const r of requests ?? []) {
      if (r.status !== "fertig") continue;
      const view = r.proposals?.find((p) => p.action === "open_view" && !p.done);
      if (!view) continue;
      const path = String((view.params as { path?: string }).path ?? "");
      if (!path.startsWith("/")) continue;
      const handledKey = `${r.id}:${view.id}`;
      if (handledViews.current.has(handledKey)) continue;
      handledViews.current.add(handledKey);
      const proposals = r.proposals.map((p) => (p.id === view.id ? { ...p, done: true } : p));
      getSupabaseClient().from("claude_requests").update({ proposals }).eq("id", r.id).then(({ error }) => {
        if (error) console.warn("open_view konnte nicht als erledigt markiert werden:", error.message);
        queryClient.invalidateQueries({ queryKey: ["claude-thread", threadId] });
      });
      // Markierung _ansicht erzwingt das Übernehmen – auch wenn Parameter fehlen (= Filter zurücksetzen)
      navigate(`${path}${path.includes("?") ? "&" : "?"}_ansicht=${Date.now()}`);
    }
  }, [requests, navigate, queryClient, threadId]);

  useEffect(() => {
    try {
      localStorage.setItem(THREAD_KEY, threadId);
    } catch {
      // Speicher nicht verfügbar
    }
  }, [threadId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [requests, open]);

  if (!identity) return null;
  const pending = requests?.some((r) => r.status === "wartet" || r.status === "laeuft");

  const send = async () => {
    const prompt = input.trim();
    if (!prompt) return;
    setSending(true);
    try {
      await sendClaudeRequest({ threadId, prompt, salesId: identity.id, context: pageContext(location.pathname) });
      setInput("");
      queryClient.invalidateQueries({ queryKey: ["claude-thread", threadId] });
    } catch (e) {
      notify(`Senden fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`, { type: "error" });
    } finally {
      setSending(false);
    }
  };

  const cancel = async (id: number) => {
    await getSupabaseClient().from("claude_requests").update({ status: "abgebrochen" }).eq("id", id);
    queryClient.invalidateQueries({ queryKey: ["claude-thread", threadId] });
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-105 transition-transform cursor-pointer"
          title="Claude fragen"
        >
          {pending ? <Loader2 className="h-6 w-6 animate-spin" /> : <MessageCircle className="h-6 w-6" />}
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 flex h-[min(640px,calc(100vh-6rem))] w-[min(420px,calc(100vw-2.5rem))] flex-col rounded-xl border border-border bg-background shadow-2xl">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <div className="flex items-center gap-2 font-medium">
              <Sparkles className="h-4 w-4 text-primary" /> Claude
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setThreadId(newThreadId())} title="Neuer Verlauf">
                <Plus className="h-3.5 w-3.5" /> Neu
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setOpen(false)} title="Schließen">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
            {!requests?.length && (
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Frag etwas zu Kunden, Events, Locations oder Aufträgen – tippen oder mit „Sprechen“ diktieren.</p>
                <p className="text-xs">
                  Beispiele: „Welche Hot-Leads haben im November ein Event?“ · „Ich habe gerade mit Cora von Zett telefoniert, sie sagen
                  nächste Woche zu – bitte Notiz und Aufgabe anlegen.“ · „Was wissen wir über Parken im InterContinental?“
                </p>
                <p className="text-xs">Claude liest die CRM-Daten nur. Änderungen schlägt es vor, du bestätigst sie per Klick.</p>
              </div>
            )}
            {requests?.map((r) => (
              <div key={r.id} className="space-y-2">
                <div className="ml-8 whitespace-pre-wrap rounded-lg bg-primary/10 px-3 py-2 text-sm">{r.prompt}</div>
                <div className="mr-4 rounded-lg bg-muted/60 px-3 py-2">
                  {r.status === "fertig" && r.answer ? (
                    <>
                      <Markdown text={r.answer} />
                      <ProposalList request={r} />
                    </>
                  ) : r.status === "fehler" ? (
                    <p className="text-sm text-destructive">Fehler: {r.error}</p>
                  ) : (
                    <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
                      <span className="inline-flex items-center gap-2">
                        {(r.status === "wartet" || r.status === "laeuft") && <Loader2 className="h-4 w-4 animate-spin" />}
                        {STATUS_TEXT[r.status]}
                      </span>
                      {(r.status === "wartet" || r.status === "laeuft") && (
                        <button type="button" className="text-xs underline cursor-pointer" onClick={() => cancel(r.id)}>
                          Abbrechen
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-border p-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Frage oder Auftrag … (Enter sendet, Shift+Enter neue Zeile)"
              rows={3}
              className="w-full resize-none rounded-md border border-input bg-background p-2 text-sm"
            />
            <div className="mt-1 flex items-center justify-between gap-2">
              <MicButton onText={(t) => setInput((v) => appendDictated(v, t))} />
              <Button size="sm" onClick={send} disabled={sending || !input.trim()}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Senden
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
